import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';
import { useAppDispatch } from '../../hooks/redux';
import {
  createAffiliateCoupon,
  deactivateAffiliateCoupon,
  fetchAffiliateCouponRedemptions,
  fetchAffiliateCoupons,
} from '../../store/slices/affiliateSlice';
import { addNotification } from '../../store/slices/notificationSlice';
import { ProductsService } from '../../api/generated/services/ProductsService';
import type { CouponView } from '../../api/generated/models/CouponView';
import { CouponCreateRequest } from '../../api/generated/models/CouponCreateRequest';
import { helper } from '../../utils/helper';
import {
  PARTNER_COUPON_MAX_MONTHS,
  PARTNER_COUPON_MAX_PERCENT,
  couponDurationLabel,
} from '../../utils/couponReasons';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Checkbox from '../ui/Checkbox';
import EmptyState from '../EmptyState';
import CopyableValue from './CopyableValue';

// Stripe promotion codes: latin letters, digits and dash only (no underscore).
const CODE_PATTERN = /^[A-Za-z0-9-]{3,64}$/;
// Generated codes skip look-alike characters (I/L/O/0/1).
const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
const GENERATED_CODE_LENGTH = 8;

const generateCode = (): string =>
  Array.from({ length: GENERATED_CODE_LENGTH }, () =>
    CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)]
  ).join('');

/** Local calendar day (YYYY-MM-DD) for the date input default. */
const todayLocal = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Date input value → ISO UTC of the local day's start (00:00) or end (23:59:59). */
const localDayToIso = (day: string, endOfDay: boolean): string => {
  const [y, m, d] = day.split('-').map(Number);
  const date = endOfDay ? new Date(y, m - 1, d, 23, 59, 59) : new Date(y, m - 1, d);
  return date.toISOString();
};

/** Category label: i18n `affiliate.category_<pluginName>` when present, raw pluginName otherwise. */
const categoryLabel = (t: (k: string, o?: any) => string, pluginName: string): string =>
  t(`affiliate.category_${pluginName}`, { defaultValue: pluginName });

interface FormState {
  code: string;
  name: string;
  discountValue: string;
  /** 1..6; 1 = first invoice only. */
  durationInMonths: string;
  startsAt: string;
  endsAt: string;
  maxRedemptions: string;
  pluginNames: string[];
}

const optionalInt = (v: string): number | null => (v.trim() === '' ? null : Number(v));

/**
 * Partner coupons section of the cabinet: list, create (Modal form),
 * deactivate (with confirm) and per-coupon redemptions (expandable row).
 * All limits (20 %, 6 months, unique code) are enforced by the backend - the
 * form only mirrors them as hints and shows the 400 `error` text.
 */
const PartnerCoupons: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { coupons, couponsLoading, couponRedemptions } = useSelector(
    (state: RootState) => state.affiliate
  );

  const [categories, setCategories] = useState<string[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>({
    code: '', name: '', discountValue: '', durationInMonths: '1', startsAt: '', endsAt: '', maxRedemptions: '', pluginNames: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<CouponView | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchAffiliateCoupons());
    // Categories = distinct `pluginName` of sellable products; add-on
    // products (`<category>_addon_*`) are not categories.
    ProductsService.getAppProductList()
      .then((products) => {
        const names = products
          .map((p) => p.pluginName)
          .filter((n) => n && !n.includes('_addon_'));
        setCategories(Array.from(new Set(names)));
      })
      .catch(() => setCategories([]));
  }, [dispatch]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const openForm = () => {
    setForm({
      code: '',
      name: '',
      discountValue: '',
      durationInMonths: '1',
      startsAt: todayLocal(),
      endsAt: '',
      maxRedemptions: '',
      // All categories selected by default.
      pluginNames: categories,
    });
    setIsFormOpen(true);
  };

  const toastError = (message: string) =>
    dispatch(addNotification({ type: 'error', title: 'Error', message }));

  /** Mirrors the backend rules; returns the first violation's message or null. */
  const validateForm = (): string | null => {
    if (!CODE_PATTERN.test(form.code.trim())) return t('affiliate.coupon_form_code_invalid');
    if (!form.name.trim()) return t('affiliate.coupon_form_name_required');
    const percent = Number(form.discountValue);
    if (!Number.isInteger(percent) || percent < 1 || percent > PARTNER_COUPON_MAX_PERCENT) {
      return t('affiliate.coupon_form_percent_invalid', { max: PARTNER_COUPON_MAX_PERCENT });
    }
    const months = Number(form.durationInMonths);
    if (!Number.isInteger(months) || months < 1 || months > PARTNER_COUPON_MAX_MONTHS) {
      return t('affiliate.coupon_form_months_invalid', { max: PARTNER_COUPON_MAX_MONTHS });
    }
    if (form.startsAt && form.endsAt && form.endsAt <= form.startsAt) {
      return t('affiliate.coupon_form_end_before_start');
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      toastError(validationError);
      return;
    }
    const code = form.code.trim();
    const percent = Number(form.discountValue);
    const months = Number(form.durationInMonths);

    // All categories selected = no restriction (empty array).
    const allSelected = categories.every((c) => form.pluginNames.includes(c));
    const request: CouponCreateRequest = {
      code,
      name: form.name.trim(),
      discountType: CouponCreateRequest.discountType.PERCENT,
      discountValue: percent,
      durationInMonths: months,
      // Day-precision inputs: local start / end of day, sent as ISO UTC.
      startsAt: form.startsAt ? localDayToIso(form.startsAt, false) : null,
      endsAt: form.endsAt ? localDayToIso(form.endsAt, true) : null,
      maxRedemptions: optionalInt(form.maxRedemptions),
      pluginNames: allSelected ? [] : form.pluginNames,
    };

    setIsSaving(true);
    try {
      await dispatch(createAffiliateCoupon(request)).unwrap();
      setIsFormOpen(false);
      dispatch(addNotification({
        type: 'success',
        title: t('affiliate.coupon_form_title'),
        message: t('affiliate.coupon_form_created', { code: code.toUpperCase() }),
      }));
    } catch (err) {
      // `unwrap()` yields the `rejectWithValue` payload (backend 400 text,
      // shown as is) or a serialized error for anything else.
      toastError(typeof err === 'string' ? err : t('affiliate.coupon_form_error_generic'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    try {
      await dispatch(deactivateAffiliateCoupon(deactivateTarget.id)).unwrap();
    } catch {
      dispatch(addNotification({ type: 'error', title: 'Error', message: t('affiliate.coupon_deactivate_error') }));
    } finally {
      setDeactivateTarget(null);
    }
  };

  const toggleRedemptions = (coupon: CouponView) => {
    if (expandedId === coupon.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(coupon.id);
    if (!couponRedemptions[coupon.id]) {
      dispatch(fetchAffiliateCouponRedemptions(coupon.id));
    }
  };

  const limitsLabel = (coupon: CouponView): string =>
    coupon.maxRedemptions != null ? t('affiliate.coupon_limit_total', { n: coupon.maxRedemptions }) : '—';

  return (
    <>
      <div className="partner-coupons-header">
        <h2 className="partner-referrals-title">{t('affiliate.coupons_title')}</h2>
        <button
          type="button"
          className="partner-coupons-add"
          onClick={openForm}
          title={t('affiliate.coupons_create_button')}
          aria-label={t('affiliate.coupons_create_button')}
        >
          +
        </button>
      </div>

      <div className="table-container">
        {coupons.length > 0 ? (
          <table className="table partner-coupons-table">
            <thead>
              <tr>
                <th>{t('affiliate.coupons_col_code')}</th>
                <th>{t('affiliate.coupons_col_name')}</th>
                <th>{t('affiliate.coupons_col_discount')}</th>
                <th>{t('affiliate.coupons_col_duration')}</th>
                <th>{t('affiliate.coupon_form_starts_at')}</th>
                <th>{t('affiliate.coupon_form_ends_at')}</th>
                <th>{t('affiliate.coupons_col_limits')}</th>
                <th>{t('affiliate.coupons_col_categories')}</th>
                <th>{t('affiliate.coupons_col_redemptions')}</th>
                <th>{t('affiliate.coupons_col_status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {coupons.map((coupon) => {
                const redemptions = couponRedemptions[coupon.id];
                const expanded = expandedId === coupon.id;
                return (
                  <React.Fragment key={coupon.id}>
                    <tr>
                      <td className="partner-coupon-code">
                        <CopyableValue value={coupon.code} copyTitle={t('affiliate.cabinet_copy')} />
                      </td>
                      <td>{coupon.name}</td>
                      <td>
                        {coupon.discountType === 'percent'
                          ? `${coupon.discountValue} %`
                          : helper.formatPriceCents(coupon.discountValue, coupon.currency || 'EUR')}
                      </td>
                      <td>{couponDurationLabel(t, coupon.duration, coupon.durationInMonths)}</td>
                      <td>{coupon.startsAt ? helper.formatDate(coupon.startsAt) : ''}</td>
                      <td>{coupon.endsAt ? helper.formatDate(coupon.endsAt) : t('affiliate.coupon_end_forever')}</td>
                      <td>{limitsLabel(coupon)}</td>
                      <td>
                        {coupon.pluginNames && coupon.pluginNames.length > 0
                          ? coupon.pluginNames.map((n) => categoryLabel(t, n)).join(', ')
                          : t('affiliate.coupons_all_categories')}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="affiliate-link-button"
                          onClick={() => toggleRedemptions(coupon)}
                          aria-expanded={expanded}
                        >
                          {coupon.redemptionCount ?? 0} {expanded ? '▴' : '▾'}
                        </button>
                      </td>
                      <td>
                        {coupon.isActive
                          ? t('affiliate.coupon_status_active')
                          : t('affiliate.coupon_status_inactive')}
                      </td>
                      <td>
                        {coupon.isActive && (
                          <button
                            type="button"
                            className="affiliate-link-button partner-coupon-deactivate"
                            onClick={() => setDeactivateTarget(coupon)}
                          >
                            {t('affiliate.coupon_deactivate_button')}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="partner-coupon-redemptions-row">
                        <td colSpan={11}>
                          {!redemptions ? (
                            <span className="partner-coupon-muted">{t('notification_loading')}</span>
                          ) : redemptions.length === 0 ? (
                            <span className="partner-coupon-muted">{t('affiliate.coupon_redemptions_empty')}</span>
                          ) : (
                            <table className="table partner-coupon-redemptions">
                              <thead>
                                <tr>
                                  <th>{t('affiliate.referrals_email')}</th>
                                  <th>{t('affiliate.coupon_redemptions_date')}</th>
                                  <th>{t('affiliate.coupon_redemptions_discount')}</th>
                                  <th>{t('affiliate.coupon_redemptions_netto')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {redemptions.map((r) => (
                                  <tr key={r.id}>
                                    <td>{r.email ?? '—'}</td>
                                    <td>{helper.formatDate(r.redeemedAt)}</td>
                                    <td>{helper.formatPriceCents(r.discountAmountCents ?? 0)}</td>
                                    <td>{helper.formatPriceCents(r.amountNetto ?? 0)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          !couponsLoading && (
            <EmptyState
              title={t('affiliate.coupons_empty_title')}
              description={t('affiliate.coupons_empty_description')}
            />
          )
        )}
      </div>

      {/* Create form */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title={t('affiliate.coupon_form_title')}
        hideCloseButton
        className="create-coupon-modal"
      >
        <form className="partner-coupon-form" onSubmit={handleSubmit}>
          <Input
            id="couponFormCode"
            label={
              <>
                {t('affiliate.coupon_form_code')}{' '}
                <button
                  type="button"
                  className="affiliate-link-button partner-coupon-generate"
                  onClick={() => setField('code', generateCode())}
                >
                  {t('affiliate.coupon_form_generate_code')}
                </button>
              </>
            }
            value={form.code}
            onChange={(v) => setField('code', v)}
            placeholder="SOMMER25"
            maxLength={64}
            required
          />
          <Input
            id="couponFormName"
            label={t('affiliate.coupon_form_name')}
            value={form.name}
            onChange={(v) => setField('name', v)}
            required
          />
          <Input
            id="couponFormPercent"
            type="number"
            label={t('affiliate.coupon_form_percent', { max: PARTNER_COUPON_MAX_PERCENT })}
            value={form.discountValue}
            onChange={(v) => setField('discountValue', v)}
            min={1}
            max={PARTNER_COUPON_MAX_PERCENT}
            step={1}
            required
          />
          <Input
            id="couponFormMonths"
            type="number"
            label={t('affiliate.coupon_form_months', { max: PARTNER_COUPON_MAX_MONTHS })}
            value={form.durationInMonths}
            onChange={(v) => setField('durationInMonths', v)}
            min={1}
            max={PARTNER_COUPON_MAX_MONTHS}
            step={1}
            required
          />
          <div className="partner-coupon-hint">{t('affiliate.coupon_form_months_hint')}</div>
          <Input
            id="couponFormStartsAt"
            type="date"
            label={t('affiliate.coupon_form_starts_at')}
            value={form.startsAt}
            onChange={(v) => setField('startsAt', v)}
            max={form.endsAt || undefined}
          />
          <Input
            id="couponFormEndsAt"
            type="date"
            label={t('affiliate.coupon_form_ends_at')}
            value={form.endsAt}
            onChange={(v) => setField('endsAt', v)}
            min={form.startsAt || undefined}
          />
          <Input
            id="couponFormMaxRedemptions"
            type="number"
            label={t('affiliate.coupon_form_max_redemptions')}
            value={form.maxRedemptions}
            onChange={(v) => setField('maxRedemptions', v)}
            min={1}
            step={1}
          />
          <div>
            {categories.map((name) => (
              <Checkbox
                key={name}
                checked={form.pluginNames.includes(name)}
                onChange={(checked) =>
                  setField(
                    'pluginNames',
                    checked
                      ? [...form.pluginNames, name]
                      : form.pluginNames.filter((n) => n !== name)
                  )
                }
              >
                {categoryLabel(t, name)}
              </Checkbox>
            ))}
            <div className="partner-coupon-hint">{t('affiliate.coupon_form_categories')}</div>
          </div>
          <div className="modal-buttons">
            <button type="button" className="button cancel" onClick={() => setIsFormOpen(false)}>
              {t('license_cancel')}
            </button>
            <button type="submit" className="button button--primary confirm" disabled={isSaving}>
              {isSaving ? t('notification_loading') : t('affiliate.coupon_form_submit')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Deactivate confirm */}
      <Modal
        isOpen={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        title={t('affiliate.coupon_deactivate_title')}
        hideCloseButton
        isCentered
      >
        <div className="modal-success">
          <div className="modal-success-text">
            {t('affiliate.coupon_deactivate_text', { code: deactivateTarget?.code ?? '' })}
          </div>
          <div className="modal-buttons">
            <button className="modal-button cancel" onClick={() => setDeactivateTarget(null)}>
              {t('license_cancel')}
            </button>
            <button className="modal-button confirm" onClick={handleDeactivate}>
              {t('affiliate.coupon_deactivate_button')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PartnerCoupons;
